import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Spin } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SharedTransportForm from '../components/SharedTransportForm';
import TransportRouteFormPageHeader from '../components/TransportRouteFormPageHeader';
import { useBackNavigation } from '../hooks/useBackNavigation';
import './TransportRouteFormPage.css';
import { omitUnchangedSharedTransportAllocationFields, planSavedCustomerOrderUnlink } from '../components/sharedTransportFormUtils';
import { unlinkOrderFromTransport } from '../modules/orders/api/orderTransportApi';
import { normalizeOrderOptions, type OrderOptionDto } from '../dto/order';
import type { SupplierOrderDto } from '../dto/supplierOrder';
import { fetchSupplierOrders } from '../modules/orders/api/supplierOrdersApi';
import { normalizeTransportRouteDetail, type TransportRouteDetailDto } from '../dto/transport';
import { canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { getTransportErrorMessage, isBlockingTransportError } from '../modules/transports/transportErrorMessages';
import { useFeedback } from '../utils/feedback';
import api from '../services/api';
import { authService } from '../services/auth';
import { submitWithOperationalException } from '../modules/operationalExceptions/operationalExceptionFlow';
import { cargoOverflowCorrectionOption } from '../modules/transports/cargoOverflowCorrection';
import { fetchTransportFormDefaults, fetchTransportRoute, updateTransportRoute } from '../modules/transports/api/transportRoutesApi';
import { readTransportReturnContext, resolveTransportReturnPath } from '../modules/transports/transportReturnContext';
import { getTransportLabel } from '../utils/transportLabel';
import { describeTransportPriceChange, showTransportSaveWarnings } from '../modules/transports/transportSaveWarnings';
import { routeAllocationKeyLabel } from '../modules/transports/transportCostSplitPreview';
import { dismissAttachFeedback } from '../modules/transports/useAttachToTransport';
import { incompleteValuationWarning, readIncompleteValuationIssues } from '../modules/orders/stockValuationSaveWarning';

const EditTransportRoute: React.FC = () => {
  const { t } = useTranslation(['transport', 'common']);
  const feedback = useFeedback();
  const { modal } = App.useApp();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const orderIdToLink = searchParams.get('orderId');
  const returnContext = readTransportReturnContext(searchParams);
  const initialOrderIds = useMemo(() => (orderIdToLink ? [orderIdToLink] : undefined), [orderIdToLink]);
  const [availableOrders, setAvailableOrders] = useState<OrderOptionDto[]>([]);
  // Etap 2 (transport przywozowy): candidates for the inbound (ZD) link.
  const [availableSupplierOrders, setAvailableSupplierOrders] = useState<SupplierOrderDto[]>([]);
  const [supplierOrdersLoading, setSupplierOrdersLoading] = useState(true);
  const [supplierOrdersLoadError, setSupplierOrdersLoadError] = useState(false);
  const [transport, setTransport] = useState<TransportRouteDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  // „Magazyn” card of the Załadunek/Rozładunek legs needs the address, as on create (review 2026-10-06).
  const [warehouseAddress, setWarehouseAddress] = useState<string | null>(null);
  const currentUser = authService.getCurrentUser();
  const canManageTransports = canManageTransportRoutes(currentUser);
  const canEditTransportCosts = authService.canEditTransportCosts();
  const canViewTransportCosts = authService.canViewTransportCosts();
  const canViewGoodsValue = authService.canViewCustomerSalePrices();

  // The load effect below must run once per truck. `t` changes identity when a
  // lazily loaded i18n namespace arrives, `navigate` when the location does:
  // as effect dependencies they refetched the truck mid-edit, and the new
  // `transport` object re-seeded the form over the operator's input.
  const latest = useRef({ t, feedback, navigate });
  latest.current = { t, feedback, navigate };

  const fetchTransport = useCallback(async (transportId: string) => {
    try {
      setTransport(await fetchTransportRoute(transportId));
    } catch (error) {
      latest.current.feedback.error(latest.current.t('transport:errorFetchingRoute'));
      latest.current.navigate('/app/transports');
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/orders');
      setAvailableOrders(normalizeOrderOptions(response.data, 'transport.edit.availableOrders'));
    } catch (error) {
      latest.current.feedback.error(latest.current.t('transport:errorFetchingOrders'));
    } finally {
      setFetching(false);
    }
  }, []);

  // Etap 2: advisory fetch — an empty candidate list still lets the operator
  // see (and keep) the inbound links already saved on this transport.
  const fetchSupplierOrdersForLinking = useCallback(async () => {
    setSupplierOrdersLoading(true);
    setSupplierOrdersLoadError(false);
    try {
      setAvailableSupplierOrders(await fetchSupplierOrders());
    } catch (error) {
      setAvailableSupplierOrders([]);
      setSupplierOrdersLoadError(true);
    } finally {
      setSupplierOrdersLoading(false);
    }
  }, []);

  // Usability round 2026-10-05: a „Dołożono …” toast from the previous page
  // must not cover this page's header.
  useEffect(() => {
    dismissAttachFeedback(feedback.message);
  }, [feedback.message]);

  useEffect(() => {
    if (!id) {
      return;
    }

    if (!canManageTransports) {
      latest.current.navigate('/app/transports', { replace: true });
      return;
    }

    void Promise.all([fetchTransport(id), fetchOrders(), fetchSupplierOrdersForLinking()]);
    void fetchTransportFormDefaults()
      .then((defaults) => setWarehouseAddress(defaults.warehouseAddress))
      .catch(() => setWarehouseAddress(null));
  }, [canManageTransports, fetchOrders, fetchSupplierOrdersForLinking, fetchTransport, id]);

  // ZK this page has already unlinked from the loaded truck (see handleSubmit).
  const unlinkedOrderIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    unlinkedOrderIdsRef.current = new Set();
  }, [transport]);
  const withoutUnlinkedOrders = (truck: TransportRouteDetailDto | null): TransportRouteDetailDto | null => {
    const unlinked = unlinkedOrderIdsRef.current;
    if (!truck || unlinked.size === 0) return truck;
    return {
      ...truck,
      routeOrders: truck.routeOrders.filter((routeOrder) => !unlinked.has(routeOrder.orderId)),
      cargoItems: (truck.cargoItems || []).filter((item) => !unlinked.has(item.orderId)),
    };
  };

  const handleSubmit = async (payload: Record<string, unknown>) => {
    if (!id) {
      return;
    }

    setLoading(true);

    try {
      // The last ZK taken off goes through the canonical unlink first: the
      // update keeps the saved ZK when `orderIds` is omitted (review 2026-10-12).
      // An unlink commits on its own: a failed PUT after it must not make the
      // next „Zapisz” unlink again (review 2026-10-12), so the plan and the
      // unchanged-field check read the truck without the ZK already unlinked.
      // (Not setTransport: a new `transport` resets the form and its edits.)
      const plan = (truck: TransportRouteDetailDto | null) => (truck
        ? planSavedCustomerOrderUnlink(payload, truck)
        : { unlinkOrderIds: [] as string[] });
      for (const orderId of plan(withoutUnlinkedOrders(transport)).unlinkOrderIds) {
        try {
          const unlinked = await unlinkOrderFromTransport(id, orderId, { modal, t });
          if (unlinked.cancelled) return;
        } catch (error: any) {
          // Already off the truck (e.g. unlinked by the save that failed): carry on.
          if (error?.response?.data?.code !== 'TRANSPORT_ORDER_NOT_LINKED') throw error;
        }
        unlinkedOrderIdsRef.current.add(orderId);
      }
      const currentTransport = withoutUnlinkedOrders(transport);
      const updatePayload = currentTransport
        ? omitUnchangedSharedTransportAllocationFields(payload, currentTransport)
        : payload;
      let overflowCorrected = false;
      const response = await submitWithOperationalException(
        (confirmedPayload) => updateTransportRoute(id, confirmedPayload),
        updatePayload,
        modal,
        t,
        { correction: cargoOverflowCorrectionOption(t, () => { overflowCorrected = true; }) },
      );
      if (!response) return;
      // Round 2 (U10): the price change is part of the one success message
      // (sticky) and stays on the truck page as a note after the redirect.
      const priceChange = canEditTransportCosts ? describeTransportPriceChange(transport, response.data) : null;
      // Round 3 (U20): a changed key gets the same „było → jest” line.
      let keyChange: { before: string; after: string } | null = null;
      try {
        const saved = normalizeTransportRouteDetail(response.data, 'transportRoutes.update');
        if (transport && saved.costAllocationMethod !== transport.costAllocationMethod) {
          keyChange = { before: routeAllocationKeyLabel(transport, t), after: routeAllocationKeyLabel(saved, t) };
        }
      } catch {
        keyChange = null;
      }
      const successText = [
        t('transport:routeUpdatedSuccessfully', { number: getTransportLabel(transport) }),
        priceChange ? t('transport:messages.priceChanged', priceChange) : null,
        keyChange ? t('transport:messages.keyChanged', keyChange) : null,
        overflowCorrected ? t('transport:overflowCorrection.applied') : null,
      ].filter(Boolean).join(' ');
      feedback.success(successText, priceChange || keyChange || overflowCorrected ? { sticky: true } : undefined);
      showTransportSaveWarnings(response.data, feedback, t, canEditTransportCosts);
      const valuationIssues = canEditTransportCosts ? readIncompleteValuationIssues(response.data) : null;
      if (valuationIssues) feedback.warning(incompleteValuationWarning(valuationIssues, t), { sticky: true });
      navigate(resolveTransportReturnPath(returnContext, `/app/transports/${id}`), {
        replace: true,
        state: priceChange ? { transportPriceChange: priceChange } : undefined,
      });
    } catch (error: any) {
      feedback.error(
        getTransportErrorMessage(error, t, 'transport:errorUpdatingRoute', {
          number: getTransportLabel(transport),
        }),
        { sticky: isBlockingTransportError(error) },
      );
    } finally {
      setLoading(false);
    }
  };

  if (!canManageTransports) {
    return null;
  }

  if (fetching || !transport) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="transport-route-form-page">
      <TransportRouteFormPageHeader
        title={t('transport:editRoute')}
        onBack={() => goBack(resolveTransportReturnPath(returnContext, `/app/transports/${id}`))}
      />

      <div className="transport-route-form-shell">
        <SharedTransportForm
          availableOrders={availableOrders}
          availableSupplierOrders={availableSupplierOrders}
          supplierOrdersLoading={supplierOrdersLoading}
          supplierOrdersLoadError={supplierOrdersLoadError}
          onRetrySupplierOrders={fetchSupplierOrdersForLinking}
          transport={transport}
          initialOrderIds={initialOrderIds}
          canEditTransportCosts={canEditTransportCosts}
          canViewGoodsValue={canViewGoodsValue}
          canViewTransportCosts={canViewTransportCosts}
          warehouseAddress={warehouseAddress}
          loading={loading}
          onCancel={() => navigate(resolveTransportReturnPath(returnContext, `/app/transports/${id}`))}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
};

export default EditTransportRoute;
